import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export interface Profile {
  first_name: string | null;
  email: string | null;
  onboarding_completed: boolean;
  subscription_tier: 'free' | 'plus' | 'family' | 'teams';
  subscription_started_at: string | null;
  subscription_expires_at: string | null;
  subscription_granted_by: 'stripe' | 'manual' | 'admin' | null;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  loading: true,
  signOut: async () => {},
  refreshProfile: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) {
      setProfile(null);
      return;
    }

    const fetchProfile = async (uid: string) => {
      const { data } = await supabase
        .from("profiles")
        .select("first_name, email, onboarding_completed, subscription_tier, subscription_started_at, subscription_expires_at, subscription_granted_by")
        .eq("user_id", uid)
        .single();

      if (data) {
        setProfile({
          first_name: data.first_name,
          email: data.email,
          onboarding_completed: data.onboarding_completed,
          subscription_tier: (data.subscription_tier as Profile['subscription_tier']) || 'free',
          subscription_started_at: data.subscription_started_at,
          subscription_expires_at: data.subscription_expires_at,
          subscription_granted_by: data.subscription_granted_by as Profile['subscription_granted_by'],
        });

        // Update last_active_at on session start
        supabase
          .from("profiles")
          .update({ last_active_at: new Date().toISOString() } as any)
          .eq("user_id", uid)
          .then(() => {});
      }
    };

    fetchProfile(userId);
  }, [session?.user?.id]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const refreshProfile = async () => {
    const userId = session?.user?.id;
    if (!userId) return;
    const { data } = await supabase
      .from("profiles")
      .select("first_name, email, onboarding_completed, subscription_tier, subscription_started_at, subscription_expires_at, subscription_granted_by")
      .eq("user_id", userId)
      .single();
    if (data) {
      setProfile({
        first_name: data.first_name,
        email: data.email,
        onboarding_completed: data.onboarding_completed,
        subscription_tier: (data.subscription_tier as Profile['subscription_tier']) || 'free',
        subscription_started_at: data.subscription_started_at,
        subscription_expires_at: data.subscription_expires_at,
        subscription_granted_by: data.subscription_granted_by as Profile['subscription_granted_by'],
      });
    }
  };

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, profile, loading, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};
